import { StaticImage } from 'gatsby-plugin-image';
import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Container,
  Badge,
  Heading,
  Divider,
  Icon,
} from '@hover/blueprint';
import { iChevronUp, iChevronDown } from '@hover/icons';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import styled from '@emotion/styled';
import { BETTER_WAY_TO_BUILD_CAROUSEL_DATA } from '../../../constants';
import { useInterval } from '../../../hooks';

const StyledSlider = styled(Slider)`
  .slick-dots {
    display: flex !important;
    flex-direction: column;
    list-style: none;
    text-align: center;
    top: 40%;
    transform: translateY(-30%);
    right: -99%;
  }
  .slick-dots li {
    height: 30px;
  }
  .slick-dots li button:before {
    color: #d5d5d5 !important;
    font-size: 10px;
    opacity: 1;
  }

  .slick-dots li.slick-active button:before {
    color: #2277d1 !important;
  }
`;

const setting = {
  dots: true,
  infinite: false,
  slidesToShow: 1,
  slidesToScroll: 1,
  vertical: true,
  verticalSwiping: true,
};

const BetterWayDesktop: React.FC = () => {
  const sliderRef = useRef<Slider>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [windowHeight, setWindowHeight] = useState<number>(
    typeof window !== 'undefined' ? window.innerHeight : 0,
  );
  const [isPlaying, setPlaying] = useState<boolean>(false);

  useEffect(() => {
    setLoading(!loading);
  }, []);

  useInterval(
    () => {
      const activeItem =
        Number(
          document
            .getElementsByClassName('slick-active')[0]
            .getAttribute('data-index'),
        ) + 1;
      const scrollContainer: HTMLElement | null =
        document.getElementById('build-container');
      if (
        activeItem < 6 &&
        sliderRef?.current &&
        scrollContainer &&
        scrollContainer.clientHeight > 0
      ) {
        window?.scrollTo({ top: windowHeight * (activeItem + 2) });
        sliderRef.current.slickGoTo(activeItem);
      }
    },
    isPlaying ? 5000 : null,
  );

  useEffect(() => {
    setWindowHeight(window.innerHeight);
    const scrollContainer: HTMLElement | null =
      document.getElementById('build-container');
    const onScroll = () => {
      if (sliderRef?.current && scrollContainer) {
        const containerBounding = scrollContainer.getBoundingClientRect();
        const containerHeight = -containerBounding.y;

        if (containerHeight < 0) {
          sliderRef.current.slickGoTo(0);
          return setPlaying(false);
        }
        setPlaying(true);
        if (containerHeight >= 0 && containerHeight <= windowHeight) {
          return sliderRef.current.slickGoTo(0);
        }
        if (
          containerHeight >= windowHeight &&
          containerHeight <= windowHeight * 2
        ) {
          return sliderRef.current.slickGoTo(1);
        }
        if (
          containerHeight >= windowHeight * 2 &&
          containerHeight <= windowHeight * 3
        ) {
          return sliderRef.current.slickGoTo(2);
        }
        if (
          containerHeight >= windowHeight * 3 &&
          containerHeight <= windowHeight * 4
        ) {
          return sliderRef.current.slickGoTo(3);
        }
        if (
          containerHeight >= windowHeight * 4 &&
          containerHeight <= windowHeight * 5
        ) {
          return sliderRef.current.slickGoTo(4);
        }
        if (
          containerHeight >= windowHeight * 5 &&
          containerHeight <= windowHeight * 6
        ) {
          sliderRef.current.slickGoTo(5);
          return setPlaying(false);
        }
        if (containerHeight > windowHeight * 6) {
          sliderRef.current.slickGoTo(5);
          return setPlaying(false);
        }
      }
      return null;
    };

    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const sliderSetting = {
    ...setting,
    appendDots: (dots: JSX.Element[]) => {
      let newDots = [...dots];
      if (sliderRef.current) {
        newDots = [
          <Icon
            cursor="pointer"
            color="primary.300"
            size="default"
            ml={100}
            mb={700}
            icon={iChevronUp}
            onClick={() => {
              const activeItem =
                Number(
                  document
                    .getElementsByClassName('slick-active')[0]
                    .getAttribute('data-index'),
                ) + 1;
              if (activeItem > 1 && sliderRef?.current) {
                window?.scrollTo({ top: windowHeight * activeItem });
                sliderRef.current?.slickPrev();
              }
            }}
          />,
          ...dots,
          <Icon
            color="primary.300"
            cursor="pointer"
            size="default"
            ml={100}
            mt={600}
            icon={iChevronDown}
            onClick={() => {
              const activeItem =
                Number(
                  document
                    .getElementsByClassName('slick-active')[0]
                    .getAttribute('data-index'),
                ) + 1;
              if (activeItem < 6 && sliderRef?.current) {
                window?.scrollTo({ top: windowHeight * (activeItem + 2) });
                sliderRef.current?.slickNext();
              }
            }}
          />,
        ];
      }
      return <ul> {newDots} </ul>;
    },
  };

  return (
    <Container
      maxW="5xl"
      overflow="hidden"
      d={{ base: 'none', desktop: 'block' }}
      id="scroll-element"
      position="relative"
    >
      <Box
        d="flex !important"
        alignItems="center"
        justifyContent="space-between"
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        mx={400}
      >
        <Box maxH="320px">
          <StaticImage
            quality={100}
            placeholder="blurred"
            width={250}
            objectFit="contain"
            alt="phone"
            src="../../../assets/images/slider-background.png"
          />
        </Box>
        <Box ml={1000} flex={1} justifyContent="center" flexDir="column" />
      </Box>
      <StyledSlider ref={sliderRef} {...sliderSetting}>
        {BETTER_WAY_TO_BUILD_CAROUSEL_DATA.map(carouselItem => (
          <Box
            key={carouselItem.index}
            d="flex !important"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box maxH="320px">{carouselItem.img}</Box>
            <Box ml={1000} flex={1} justifyContent="center" flexDir="column">
              <Badge
                colorScheme="brandNavy"
                variant="brand"
                d="flex"
                alignItems="center"
                justifyContent="center"
                w={400}
                h={400}
                borderRadius="50%"
              >
                <Heading size={500} marginBottom={0}>
                  {carouselItem.index + 1}
                </Heading>
              </Badge>
              <Divider
                borderWidth="2px"
                my={600}
                w={400}
                opacity={1}
                color="brandNavy.300"
              />
              <Heading maxW="380px" isLight size={500}>
                {carouselItem.body}
              </Heading>
            </Box>
          </Box>
        ))}
      </StyledSlider>
    </Container>
  );
};

export default BetterWayDesktop;
